/**
 * Client-side security checks for files and URLs before upload/send
 */

// Dangerous file extensions
const DANGEROUS_EXTENSIONS = new Set([
  'exe','bat','cmd','com','scr','pif','vbs','vbe','js','jse','wsf','wsh',
  'ps1','ps2','psm1','psd1','msi','msp','mst','reg','inf','lnk','url',
  'jar','class','sh','bash','zsh','php','asp','aspx','cgi','pl','py',
  'rb','lua','htaccess','dll','sys','drv','ocx','cpl','msc','hta',
]);

// Allowed MIME types
const ALLOWED_MIME = new Set([
  'image/jpeg','image/png','image/gif','image/webp','image/svg+xml',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain','text/csv',
  'application/zip','application/x-zip-compressed',
]);

// Magic bytes signatures for dangerous files
const DANGEROUS_SIGNATURES = [
  { bytes: [0x4D, 0x5A],                  label: 'PE Executable (EXE/DLL)' },
  { bytes: [0x7F, 0x45, 0x4C, 0x46],      label: 'ELF Executable' },
  { bytes: [0xCA, 0xFE, 0xBA, 0xBE],      label: 'Java Class' },
  { bytes: [0x50, 0x4B, 0x03, 0x04],      label: 'ZIP/Office — needs ext check' }, // ZIP (not inherently bad)
];

// Suspicious URL patterns
const SUSPICIOUS_URL_PATTERNS = [
  /javascript:/i,
  /data:text\/html/i,
  /vbscript:/i,
  /on\w+\s*=/i,                           // onload= onclick= etc.
  /<script/i,
  /\.(exe|bat|cmd|msi|scr|vbs|ps1)(\?|$)/i,
  /(bit\.ly|tinyurl\.com|t\.co|goo\.gl|ow\.ly)\/[a-z0-9]+$/i, // shorteners (warn only)
  /([a-z0-9\-]+\.){2,}(tk|ml|ga|cf|gq|pw|top|xyz|club)(\/|$)/i, // suspicious TLDs
];

/**
 * Read first N bytes of a File as Uint8Array
 */
function readFileHeader(file, bytes = 8) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(new Uint8Array(e.target.result));
    reader.onerror = () => resolve(new Uint8Array());
    reader.readAsArrayBuffer(file.slice(0, bytes));
  });
}

/**
 * Validate a single file before upload
 * Returns { safe: bool, reason: string|null }
 */
export async function validateFile(file) {
  const name = file.name || '';
  const ext  = name.split('.').pop().toLowerCase();
  const mime = file.type || '';

  // 1. Extension check
  if (DANGEROUS_EXTENSIONS.has(ext)) {
    return { safe: false, reason: `نوع الملف "${ext}" غير مسموح به لأسباب أمنية` };
  }

  // 2. MIME type check
  if (mime && !ALLOWED_MIME.has(mime)) {
    return { safe: false, reason: `نوع الملف (${mime || ext}) غير مسموح به` };
  }

  // 3. Magic bytes check
  const header = await readFileHeader(file, 8);
  for (const sig of DANGEROUS_SIGNATURES) {
    const match = sig.bytes.every((b, i) => header[i] === b);
    if (match) {
      // ZIP is only dangerous if it's an EXE disguised — skip ZIP warning for allowed types
      if (sig.label.includes('ZIP') && ['zip','docx','xlsx','pptx'].includes(ext)) continue;
      if (!sig.label.includes('ZIP')) {
        return { safe: false, reason: `الملف يبدو أنه "${sig.label}" وهو غير مسموح` };
      }
    }
  }

  // 4. Size check (10MB)
  if (file.size > 10 * 1024 * 1024) {
    return { safe: false, reason: `حجم الملف يتجاوز 10MB` };
  }

  return { safe: true, reason: null };
}

/**
 * Validate all files in an array
 * Returns { safe: bool, reason: string|null }
 */
export async function validateFiles(files) {
  for (const file of files) {
    const result = await validateFile(file);
    if (!result.safe) return { safe: false, reason: `${file.name}: ${result.reason}` };
  }
  return { safe: true, reason: null };
}

/**
 * Scan text message for suspicious URLs/patterns
 * Returns { safe: bool, reason: string|null, warning: string|null }
 */
export function scanMessageText(text) {
  if (!text) return { safe: true, reason: null, warning: null };

  // XSS patterns
  if (/<script|javascript:|vbscript:|on\w+\s*=/i.test(text)) {
    return { safe: false, reason: 'الرسالة تحتوي على كود خطير ولن يتم إرسالها' };
  }

  // Extract URLs from text
  const urlRegex = /https?:\/\/[^\s"'<>]+/gi;
  const urls = text.match(urlRegex) || [];

  for (const url of urls) {
    for (const pattern of SUSPICIOUS_URL_PATTERNS) {
      if (pattern.test(url)) {
        // Shorteners = warning only, not block
        if (/(bit\.ly|tinyurl|t\.co|goo\.gl|ow\.ly)/.test(url)) {
          return { safe: true, reason: null, warning: `الرسالة تحتوي على رابط مختصر — تأكد أنه آمن` };
        }
        return { safe: false, reason: `الرسالة تحتوي على رابط مشبوه: ${url}` };
      }
    }
  }

  return { safe: true, reason: null, warning: null };
}
